import {
  Box,
  Chip,
  LinearProgress,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DBRequest, useConfig } from '../../../api/api';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import ExoDialog from '../../../components/exo/ExoDialog';
import TypeSkeleton from '../../../components/form/form-fields/TypeSkeleton';
import { tokens } from '../../../global/theme/tokens';
import {
  secondsToHours,
  workPackageSharePercent,
  packageProgressPercent,
  packageProgressColor,
  formatPercent,
} from './workTimeReportUtils';

const WorkTimeReportWorkPackagesDialog = ({
  open,
  onClose,
  userId,
  projectId,
  user,
  project,
  startDate,
  endDate,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !config) return;
    setData(null);
    DBRequest({
      config,
      path: `reports/work-time/${userId}/${projectId}?startDate=${startDate}&endDate=${endDate}`,
      onResponse: (res) => setData(res?.data ?? null),
    });
  }, [open, config, userId, projectId, startDate, endDate]);

  return (
    <ExoDialog open={open} onClose={onClose} limitWidth maxWidth="900px">
      <Box className="flex flex-col gap-3 p-4">
        <Box
          className="flex flex-col md:flex-row gap-3 p-3 rounded-lg backdrop-blur-lg"
          sx={{ bgcolor: colors.card }}
        >
          <Box className="flex items-center gap-2 flex-1">
            <ExoAvatar
              picture={user?.picture?.temporaryUrl}
              name={`${user?.firstName || ''} ${user?.lastName || ''}`}
              size="40px"
            />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              {user?.position && (
                <Typography variant="caption" sx={{ opacity: 0.6, lineHeight: 1.2 }}>
                  {user.position}
                </Typography>
              )}
              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.3 }}>
                {t('Your hours in project')}:{' '}
                <strong>
                  {secondsToHours(data?.userWorkedTimeInSeconds || 0)} h
                </strong>
              </Typography>
            </Box>
          </Box>

          <Box className="flex items-center gap-2 flex-1">
            {project?.image ? (
              <img
                src={project.image.temporaryUrl}
                alt=""
                className="w-10 h-10 object-contain rounded"
              />
            ) : (
              <ExoAvatar name={project?.name} size="40px" type="rounded" />
            )}
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {project?.nr && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ opacity: 0.6, fontFamily: 'monospace', mr: 1 }}
                  >
                    #{project.nr}
                  </Typography>
                )}
                {project?.name}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {t('Billable hours')}:{' '}
                <strong>
                  {secondsToHours(data?.userBillableWorkedTimeInSeconds || 0)} h
                </strong>
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography variant="caption" sx={{ opacity: 0.7, px: 1 }}>
          {t('Work packages')}
        </Typography>

        {!data ? (
          <TypeSkeleton n={4} />
        ) : data.workPackages?.length === 0 ? (
          <Box
            className="p-6 rounded-lg backdrop-blur-lg text-center"
            sx={{ bgcolor: colors.card }}
          >
            <Typography sx={{ opacity: 0.7 }}>{t('No work packages')}</Typography>
          </Box>
        ) : (
          [...data.workPackages]
            .sort(
              (a, b) =>
                (b.userWorkedTimeInSeconds || 0) - (a.userWorkedTimeInSeconds || 0),
            )
            .map((wp) => {
              const share = workPackageSharePercent(wp);
              const progress = packageProgressPercent(wp);
              const progressColor = packageProgressColor(progress, colors);

              return (
                <Box
                  key={wp.workPackage.id}
                  className="flex flex-col gap-2 p-3 rounded-lg backdrop-blur-lg"
                  sx={{ bgcolor: colors.card }}
                >
                  <Box className="flex items-center gap-3 flex-wrap">
                    {wp.workPackage.icon ? (
                      <img
                        src={wp.workPackage.icon.temporaryUrl}
                        alt=""
                        className="w-7 h-7 object-contain"
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: colors.glass,
                          borderRadius: 1,
                          flexShrink: 0,
                        }}
                      />
                    )}

                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.6,
                        fontFamily: 'monospace',
                        flexShrink: 0,
                      }}
                    >
                      #{wp.workPackage.id}
                    </Typography>

                    <Typography
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {wp.workPackage.title}
                    </Typography>

                    {wp.workPackage.isBillable && (
                      <Tooltip title={t('Billable')}>
                        <Chip
                          size="small"
                          icon={<AttachMoneyIcon sx={{ fontSize: 16 }} />}
                          label={t('Billable')}
                          sx={{
                            bgcolor: `${colors.success}25`,
                            color: colors.success,
                            border: `1px solid ${colors.success}50`,
                            fontWeight: 600,
                            '.MuiChip-icon': { color: colors.success },
                          }}
                        />
                      </Tooltip>
                    )}

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, minWidth: 60, textAlign: 'right' }}
                    >
                      {secondsToHours(wp.userWorkedTimeInSeconds)} h
                    </Typography>

                    <Box className="flex items-center gap-2 min-w-[120px]">
                      <Box
                        sx={{
                          width: 80,
                          height: 4,
                          bgcolor: colors.glass,
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${share === null ? 0 : Math.min(share, 100)}%`,
                            height: '100%',
                            bgcolor: colors.info,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ opacity: 0.7, minWidth: 40 }}>
                        {formatPercent(share)}
                      </Typography>
                    </Box>
                  </Box>

                  {wp.workPackage.estimatedTimeSeconds ? (
                    <Box className="flex items-center gap-2 pl-10">
                      <Typography variant="caption" sx={{ opacity: 0.7, minWidth: 130 }}>
                        {t('Package progress')}:{' '}
                        {secondsToHours(wp.workPackage.totalTimeWorkedSeconds)} h /{' '}
                        {secondsToHours(wp.workPackage.estimatedTimeSeconds)} h
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(progress ?? 0, 100)}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: colors.glass,
                          '& .MuiLinearProgress-bar': {
                            bgcolor: progressColor,
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: progressColor, minWidth: 50, textAlign: 'right' }}
                      >
                        {formatPercent(progress)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ opacity: 0.5, pl: 5 }}>
                      {secondsToHours(wp.workPackage.totalTimeWorkedSeconds)} h{' '}
                      {t('total worked')}
                    </Typography>
                  )}
                </Box>
              );
            })
        )}
      </Box>
    </ExoDialog>
  );
};

export default WorkTimeReportWorkPackagesDialog;
